import React, { useMemo, useState } from "react";
import {
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Box,
} from "@chakra-ui/react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import Card from "components/card/Card";
import UserDetails from "./UserDetails";
import { FiMoreVertical, FiTrash2 } from "react-icons/fi";
import DeleteModal from "components/DeleteModal/DeleteModal";
import { useDispatch } from 'react-redux';
import { deleteUser, toggleUserStatus } from '../../../../features/users/actions';
import { useToastNotification } from 'utils/toast-utils';

export default function UsersTable(props) {
  const { columnsData, tableData, onDetailsToggle } = props;
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const dispatch = useDispatch();
  const { showToast } = useToastNotification();

  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => tableData, [tableData]);

  const tableInstance = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: 1,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    initialState,
  } = tableInstance;
  // initialState.pageSize = 7;

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'green.500';
      case 'pending':
        return 'gray.500';
      case 'inactive':
        return 'orange.500';
      default:
        return 'gray.500';
    }
  };

  const getBackgroundColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'green.100';
      case 'pending':
        return 'gray.100';
      case 'inactive':
        return 'orange.100';
      default:
        return 'gray.100';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setShowDetails(true);
    onDetailsToggle(true);
  };

  const handleDeleteClick = (user) => {
    console.log("🚀 ~ handleDeleteClick ~ user:", user)
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteUser(userToDelete._id)).unwrap();
      showToast('Success', 'User deleted successfully', 'success');
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    } catch (error) {
      showToast('Error', error.message || 'Failed to delete user', 'error');
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await dispatch(toggleUserStatus({
        userId: user._id,
        activate: user.status.toLowerCase() !== 'active'
      })).unwrap();
      showToast(
        'Success',
        `User ${user.status.toLowerCase() === 'active' ? 'deactivated' : 'activated'} successfully`,
        'success'
      );
    } catch (error) {
      showToast('Error', error.message || 'Failed to update user status', 'error');
    }
  };

  if (showDetails) {
    return (
      <UserDetails
        userData={selectedUser}
        onBack={() => {
          setShowDetails(false);
          setSelectedUser(null);
          onDetailsToggle(false);
        }}
      />
    );
  }

  return (
    <>
      <Card
        direction="column"
        w="100%"
        px="0px"
        pt="0px"
        overflowX={{ sm: "scroll", lg: "hidden" }}
      >
        <Box>
          <Table {...getTableProps()} variant="simple" color="gray.500">
            <Thead>
              {headerGroups.map((headerGroup, index) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <Th
                      {...column.getHeaderProps()}
                      pe="10px"
                      key={index}
                      borderColor={borderColor}
                    >
                      <Text color="gray.600" fontSize="12px" fontWeight="700">
                        {column.render("Header")}
                      </Text>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <Tr 
                    {...row.getRowProps()} 
                    key={index}
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                    onClick={(e) => {
                      if (!e.target.closest('.chakra-menu__menu-button')) {
                        handleViewDetails(row.original);
                      }
                    }}
                  >
                    {row.cells.map((cell, index) => {
                      let data = cell.value;

                      if (cell.column.Header === "NAME") {
                        data = (
                          <Text
                            color="purple.500"
                            fontSize="sm"
                            fontWeight="500"
                            cursor="pointer"
                            _hover={{
                              textDecoration: "underline"
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(row.original);
                            }}
                          >
                            {cell.value || 'N/A'}
                          </Text>
                        );
                      } else if (cell.column.Header === "EMAIL") {
                        data = (
                          <Text
                            color="purple.500"
                            fontSize="sm"
                            fontWeight="500"
                            cursor="pointer"
                            _hover={{
                              textDecoration: "underline"
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(row.original);
                            }}
                          >
                            {cell.value}
                          </Text>
                        );
                      } else if (cell.column.Header === "STATUS") {
                        data = (
                          <Text
                            px={4}
                            py={1.5}
                            borderRadius="full"
                            bg={row.original.status.toLowerCase() === 'active' ? 'green.100' : 'red.100'}
                            color={row.original.status.toLowerCase() === 'active' ? 'green.500' : 'red.500'}
                            fontSize="sm"
                            fontWeight="500"
                            display="inline-block"
                            textTransform="capitalize"
                          >
                            {cell.value?.toLowerCase()}
                          </Text>
                        );
                      } else if (cell.column.Header === "CREATED AT") {
                        data = (
                          <Text color={textColor} fontSize="sm" fontWeight="500">
                            {formatDate(cell.value)}
                          </Text>
                        );
                      } else if (cell.column.Header === "ACTION") {
                        const shouldCenter = page.length < 4;
                        data = (
                          <Box onClick={(e) => e.stopPropagation()}>
                            <Menu placement={shouldCenter ? "auto" : "bottom"}>
                              <MenuButton
                                as={IconButton}
                                icon={<FiMoreVertical />}
                                variant="ghost"
                                size="sm"
                                aria-label="More options"
                              />
                              <MenuList
                                minWidth="125px"
                                px=".75rem"
                                boxShadow="0px 4px 16px 0px rgba(0, 0, 0, 0.10)"
                                zIndex="dropdown"
                              >
                                <MenuItem my="1" color="#202224" fontWeight="400" fontSize="14px" onClick={() => handleViewDetails(row.original)}>
                                  View Details
                                </MenuItem>
                                <MenuItem
                                  my="1"
                                  color="#202224"
                                  fontWeight="400"
                                  fontSize="14px"
                                  onClick={() => handleToggleStatus(row.original)}
                                >
                                  {row.original.status.toLowerCase() === 'active' ? 'Deactivate' : 'Activate'}
                                </MenuItem>
                                <MenuItem my="1" color="red.500" fontWeight="400" fontSize="14px" onClick={() => handleDeleteClick(row.original)}>
                                  Delete
                                </MenuItem>
                              </MenuList>
                            </Menu>
                          </Box>
                        );
                      }

                      return (
                        <Td
                          {...cell.getCellProps()}
                          key={index}
                          fontSize={{ sm: "14px" }}
                          minW={{ sm: "150px", md: "200px", lg: "auto" }}
                          borderColor={borderColor}
                        >
                          {data}
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </Card>

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleDeleteConfirm}
        icon={FiTrash2}
        heading="Delete User"
        description={`Are you sure you want to delete this user? This action cannot be undone.`}
        deleteButtonText="Delete User"
        cancelButtonText="Cancel"
        type="delete"
      />
    </>
  );
}
